import React from 'react'

import { Box, Chip, Link } from '@mui/material'

import { LockerOrderListItem } from '../../../../redux/states/admin/lockers/getLockerOrders/getLockerOrders.types'
import { TableColumn } from '../../../../shared/components/Table/Table.types'
import date, { dateTimeFormats } from '../../../../shared/functions/Date/date.functions'
import { formatAdminLockerName } from '../../../../shared/functions/Locker/locker.functions'
import { getStatusColor } from '../../../../shared/functions/Status/status.functions'
import { changeCase } from '../../../../shared/functions/String/string.functions'
import styles from './LockerOrders.styles'
import { LockerOrderLinkActions, LockerOrderStatusTab } from './LockerOrders.types'

const formatDate = (value: string | null) => (value ? date(value).format(dateTimeFormats.date.medium) : '—')

/**
 * Drop-off and pickup usually happen on the SAME day, so a date-only pair renders
 * as the same value twice and reads like a bug. The time is what actually
 * distinguishes the two events — and it is what support is asked about ("when
 * did they collect it?").
 */
const formatMoment = (value: string | null) => (value ? date(value).format('MMM d, h:mm a') : '—')

/**
 * Every status a locker order can be in, each with its own tab, after one for all of them. The
 * endpoint filters by status, so each tab pages through its own status rather than the loaded page.
 */
export const lockerOrderStatusTabs: LockerOrderStatusTab[] = [
  { label: 'All' },
  { label: 'Dropoff Pending', status: 'dropoff_pending' },
  { label: 'Pickup Pending', status: 'pickup_pending' },
  { label: 'Completed', status: 'completed' },
  { label: 'Cancelled', status: 'cancelled' }
]

/**
 * A link inside a clickable row. It stops the click there, so the row's own click (open the
 * order) does not run as well and navigate a second time.
 */
const RowLink: React.FC<{ label: string; ariaLabel: string; onOpen: () => void }> = ({ label, ariaLabel, onOpen }) => (
  <Link
    component="button"
    type="button"
    underline="hover"
    aria-label={ariaLabel}
    sx={styles.rowLink}
    onClick={(event: React.MouseEvent) => {
      event.stopPropagation()
      onOpen()
    }}
  >
    {label}
  </Link>
)

/**
 * Paired values share a cell rather than each taking a column. Support needs all
 * of this at once, and at 1440px ten columns pushed the SmartLocker id — the one
 * thing they quote to the locker vendor — off the right edge entirely.
 *
 * The order number, the item and the trip are links: support starts from whichever one the
 * person writing in quotes, and each opens its own admin page.
 */
export const getLockerOrderTableColumns = (actions: LockerOrderLinkActions): TableColumn<LockerOrderListItem>[] => [
  {
    // First, because it is what a shopper quotes, and it carries the trip so the
    // second question ("which flight?") is answered without leaving the row.
    key: 'order',
    label: 'Order / Trip',
    render: (row) => (
      <Box sx={styles.stackedCell}>
        <Box sx={styles.primaryLine}>
          {row.orderNumber && row.shopper.hurrierId ? (
            <RowLink
              label={`Order #${row.orderNumber}`}
              ariaLabel={`Open order ${row.orderNumber}`}
              onOpen={() => actions.onOpenOrder(row)}
            />
          ) : (
            'Order —'
          )}
        </Box>
        <Box sx={styles.secondaryLine}>
          {row.trip ? (
            <>
              {row.traveler.hurrierId ? (
                <RowLink
                  label={`Trip #${row.trip.id}`}
                  ariaLabel={`Open trip ${row.trip.id} details`}
                  onOpen={() => actions.onOpenTrip(row)}
                />
              ) : (
                `Trip #${row.trip.id}`
              )}
              {` · ${row.trip.origin ?? '?'} → ${row.trip.destination ?? '?'} · ${formatDate(row.trip.travelDate)}`}
            </>
          ) : (
            'No trip'
          )}
        </Box>
      </Box>
    )
  },
  {
    // The column that stops one order's several locker orders reading as
    // duplicated rows: a locker order is created PER ITEM, so this is what
    // actually differs between them.
    key: 'item',
    label: 'Item',
    render: (row) => {
      const lockerDetails = [
        row.item?.doorId
          ? formatAdminLockerName({ doorId: row.item.doorId, lockerNumber: row.item.lockerNumber })
          : null,
        row.externalOrderId
      ]
        .filter(Boolean)
        .join(' · ')

      return (
        <Box sx={{ ...styles.stackedCell, ...styles.itemCell }}>
          <Box sx={styles.primaryLine}>{row.item?.title ?? '—'}</Box>
          <Box sx={styles.secondaryLine}>
            {row.item && row.orderNumber && row.shopper.hurrierId ? (
              <RowLink
                label={`Item #${row.item.itemRequestId}`}
                ariaLabel={`Open item ${row.item.itemRequestId} in order ${row.orderNumber}`}
                onOpen={() => actions.onOpenItem(row)}
              />
            ) : (
              row.item && `Item #${row.item.itemRequestId}`
            )}
            {row.item ? ` · ${lockerDetails}` : lockerDetails}
          </Box>
        </Box>
      )
    }
  },
  {
    key: 'orderStatus',
    label: 'Status',
    render: (row) => (
      <Chip
        label={changeCase(row.orderStatus.replace(/_/g, ' ')).sentence}
        color={getStatusColor(row.orderStatus)}
        size="small"
      />
    )
  },
  {
    key: 'people',
    label: 'Shopper / Traveler',
    render: (row) => (
      <Box sx={styles.stackedCell}>
        <Box sx={styles.primaryLine}>{`${row.shopper.firstName} ${row.shopper.lastName}`.trim() || '—'}</Box>
        <Box sx={styles.secondaryLine}>{`${row.traveler.firstName} ${row.traveler.lastName}`.trim() || '—'}</Box>
      </Box>
    )
  },
  {
    key: 'lockerBank',
    label: 'Locker Bank',
    render: (row) => (
      <Box sx={{ ...styles.stackedCell, ...styles.bankCell }}>
        <Box sx={styles.primaryLine}>{row.lockerBank.name}</Box>
        <Box sx={styles.secondaryLine}>{row.lockerBank.cityName}</Box>
      </Box>
    )
  },
  {
    key: 'timeline',
    label: 'Dropped Off / Picked Up',
    render: (row) => (
      <Box sx={{ ...styles.stackedCell, ...styles.timelineCell }}>
        <Box sx={styles.primaryLine}>{formatMoment(row.dropoffTs)}</Box>
        <Box sx={styles.secondaryLine}>{formatMoment(row.pickupTs)}</Box>
      </Box>
    )
  }
]
